import Image from 'next/image';
import { Dispatch, SetStateAction } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { useCreateCapitalRequestMutation } from '../../../api';
import { Button } from '../../../ui';
import { toTitleCase } from '../../../utils';

export const Review = ({
  capitalRequestInput,
  setCurrentStep,
  isLoading,
}: {
  capitalRequestInput: CapitalRequestInput;
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  isLoading: boolean;
}) => {
  const [createCr] = useCreateCapitalRequestMutation();

  const handleSubmit = async () => {
    await createCr(capitalRequestInput);
    setCurrentStep('CONFIRMATION');
  };

  return (
    <div className="flex gap-8 h-[750px] overflow-auto">
      <div className="flex flex-col gap-8 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold text-text-primary">
            Review Capital Request
          </span>
          <span className="text-base font-normal text-text-secondary">
            Please review all the information you have provided to ensure its
            accuracy before submitting your capital request. Carefully check
            each section below to confirm that all details are correct and
            complete.
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">
                Types of Capital
              </span>
              <span className="text-sm">
                You&apos;re interested in exploring these types of capital:
              </span>
              <div className="flex gap-2 flex-wrap">
                {capitalRequestInput.capitalTypes.map((type, index) => (
                  <div
                    key={index}
                    className="rounded-2xl bg-purple-50 text-purple-700 px-3 py-2"
                  >
                    <span className="text-sm">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">Growth Plans</span>
              <div className="flex gap-2 flex-wrap">
                {capitalRequestInput.growthPlans.map((growthPlan, index) => (
                  <div
                    key={index}
                    className="rounded-2xl bg-gray-100 px-3 py-2"
                  >
                    <div className="flex gap-2">
                      <Image
                        src={'/icons/file-gray.svg'}
                        alt="PDF"
                        width={20}
                        height={20}
                      />
                      <span className="text-sm">{growthPlan.fileName}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">
                Financial Statements
              </span>
              <div className="flex gap-2 flex-wrap">
                {capitalRequestInput.financialStatements.map(
                  (financialStatement, index) => (
                    <div
                      key={index}
                      className="rounded-2xl bg-gray-100 px-3 py-2"
                    >
                      <div className="flex gap-2">
                        <Image
                          src={'/icons/file-gray.svg'}
                          alt="PDF"
                          width={20}
                          height={20}
                        />
                        <span className="text-sm">
                          {financialStatement.file.fileName}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">EBITDA</span>
              <span>
                Your organization has a{' '}
                {capitalRequestInput.positiveEBITDA ? 'positive' : 'negative'}{' '}
                EBITDA.
              </span>
              {capitalRequestInput.positiveEBITDA &&
                (capitalRequestInput.EBITDARange.unknown ? (
                  <span>Your current EBITDA is unknown.</span>
                ) : capitalRequestInput.EBITDARange.unprovided ? (
                  <span>Your choose not to provide your current EBITDA.</span>
                ) : (
                  <span>
                    Your current EBITDA is{' '}
                    {capitalRequestInput.EBITDARange.max == 0 && 'more than'}
                    {capitalRequestInput.EBITDARange.min == 0 &&
                      'less than'}{' '}
                    {capitalRequestInput.EBITDARange.min > 0 &&
                      `$${capitalRequestInput.EBITDARange.min} million`}
                    {capitalRequestInput.EBITDARange.min > 0 &&
                      capitalRequestInput.EBITDARange.max > 0 &&
                      ' - '}
                    {capitalRequestInput.EBITDARange.max > 0 &&
                      `$${capitalRequestInput.EBITDARange.max} million`}
                    .
                  </span>
                ))}
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">
                Total Amount Requested
              </span>
              <div className="rounded-2xl bg-success-50 text-success-700 px-3 py-2 w-fit">
                <span className="text-sm">
                  ${capitalRequestInput.capitalNeed}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex flex-col gap-1 w-full">
              <span className="font-bold text-text-primary">
                Detailed Request Breakdown
              </span>
              <div className="flex gap-2 flex-wrap">
                {capitalRequestInput.capitalNeedList.map(
                  (item, index) =>
                    item.amount > 0 && (
                      <div
                        className="rounded-2xl bg-primary-50 text-primary-700 px-3 py-2 w-fit"
                        key={index}
                      >
                        <span className="text-sm">
                          {toTitleCase(item.description.split('_').join(' '))}:
                          ${item.amount}
                        </span>
                      </div>
                    )
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('BREAKDOWN')}
            />
          </div>
          <div>
            <Button
              text="Submit"
              variant={isLoading ? 'disabled' : 'primary'}
              suffixIcon="/icons/arrow_right.svg"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src={'/create_sr.png'}
          alt="Supplier Request Image"
          priority
          height={570}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
